import { useCheckoutService } from "@/rtkQuery/features/hooks/useCheckoutService";
import { ShimmerEffect, Typography } from "@/design-system/Atoms";
import { AppEmptyState, AppErrorState } from "@/design-system/Feedbacks";
import { useState, useEffect } from "react";
import { useCartService } from "@/rtkQuery/features/hooks/useCartService";

interface CouponData {
    Coupon: string;
    Discount: number;
}

interface CheckoutOrderSummarySectionProps {
    appliedCoupon?: CouponData | null;
    currentStep?: string;
}

const safeToNumber = (value: any): number => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
        const parsed = parseFloat(value);
        return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
};

const formatCurrency = (value: any): string => {
    const numValue = safeToNumber(value);
    return numValue.toFixed(2);
};

const CheckoutOrderSummarySection = ({
    appliedCoupon,
    currentStep = "user-info"
}: CheckoutOrderSummarySectionProps) => {
    const [couponDiscount, setCouponDiscount] = useState<CouponData | null>(null);
    const { cartData, cartIsLoading } = useCartService();

    const {
        recentOrders,
        recentOrdersLoading,
        recentOrdersError,
        recentOrdersErrorMessage,
    } = useCheckoutService();

    useEffect(() => {
        if (appliedCoupon) {
            setCouponDiscount(appliedCoupon);
        }
    }, [appliedCoupon]);

    const isConfirmationStep = currentStep === 'confirmation';

    const orderDetails = isConfirmationStep ? recentOrders : cartData?.products?.[0];
    const isLoading = isConfirmationStep ? recentOrdersLoading : cartIsLoading;
    const hasError = isConfirmationStep ? recentOrdersError : false;
    const errorMessage = isConfirmationStep ? recentOrdersErrorMessage : '';

    const isValidOrder = orderDetails && typeof orderDetails === 'object';

    const productName = isConfirmationStep
        ? orderDetails?.ProductName
        : orderDetails?.name || orderDetails?.ProductName;

    const rawPrice = isConfirmationStep
        ? orderDetails?.ProductPrice
        : orderDetails?.price || orderDetails?.ProductPrice;
    const productPrice = safeToNumber(rawPrice);

    const productImage = isConfirmationStep
        ? orderDetails?.Image
        : orderDetails?.image || orderDetails?.Image;

    const subscriptionDuration = isConfirmationStep
        ? orderDetails?.SubscriptionDuration
        : orderDetails?.subscriptionDuration || orderDetails?.SubscriptionDuration;

    const discountAmount = safeToNumber(couponDiscount?.Discount);

    const finalTotal = Math.max(0, productPrice - discountAmount);

    const getSubscriptionText = (duration: number) => {
        switch (duration) {
            case 1:
                return 'One Time Payment';
            case 30:
                return 'Monthly Subscription (30 days)';
            case 60:
                return 'Bi-Monthly Subscription (60 days)';
            default:
                return 'One Time Payment';
        }
    };

    return (
        <aside className="sticky top-0">
            <div className="bg-[#f6f7f9] p-8 rounded-[20px] w-full">
                <Typography as="h3" className="text-[1.5rem] mb-5 font-bold">
                    Order Summary
                </Typography>

                {isLoading ? (
                    <>
                        <ShimmerEffect className="h-[136px] w-full mb-5" />
                        <ShimmerEffect className="h-[30px] w-full mb-2" />
                        <ShimmerEffect className="h-[30px] w-full mb-2" />
                        <ShimmerEffect className="h-[36px] w-full mb-2" />
                    </>
                ) : !isValidOrder ? (
                    <AppEmptyState
                        message={isConfirmationStep ? "No recent orders found." : "No items in cart."}
                        minWidth="20vw"
                    />
                ) : hasError ? (
                    <AppErrorState message={errorMessage} />
                ) : (
                    <>
                        <div className="cart-item bg-white p-5 rounded-lg mb-5 text-[1.25rem]">
                            <div className="flex items-center gap-4">
                                <img
                                    src={`/images/products/${productImage}`}
                                    alt={productName}
                                    width={80}
                                    height={80}
                                    className="w-[80px] h-auto object-contain"
                                />
                                <div>
                                    <p>{productName}</p>
                                </div>
                            </div>

                            <p className="flex justify-between pt-5 text-[1.25rem] mb-0">
                                Price: <span>${formatCurrency(productPrice)}</span>
                            </p>
                        </div>

                        <p className="flex justify-between mb-2 text-[1.25rem]">
                            Product: <span>${formatCurrency(productPrice)}</span>
                        </p>

                        <p className="flex justify-between mb-2 text-[1.25rem]">
                            Discount:
                            <span className={discountAmount > 0 ? "text-green-600" : ""}>
                                ${formatCurrency(discountAmount)}
                            </span>
                        </p>

                        <p className="flex justify-between text-[1.5rem] font-bold">
                            Total: <span>${formatCurrency(finalTotal)}</span>
                        </p>

                        <hr className="my-4 opacity-25" />

                        <p className="text-[1.25rem]">
                            <strong>Order Details:</strong>
                            <br />
                            {getSubscriptionText(subscriptionDuration || 1)}
                        </p>
                    </>
                )}
            </div>
        </aside>
    );
};

export default CheckoutOrderSummarySection;